import { DataTexture, FloatType, NearestFilter, RGFormat } from 'three';
import { TILE, tileKey } from '@cloudpearl/assets/worldgen';

const SIZE = 256;
const SNAP = 16;

/**
 * A 256 m × 256 m window of ground height + coverage at 1 m resolution, addressed toroidally
 * (texel = world metre mod 256), around the camera. GPU grass, flowers and blowing sand read it
 * with texelFetch to plant themselves on the terrain. Filled from LOD-0 terrain tiles as they
 * stream in — no extra height evaluation on the main thread.
 */
export class LocalHeightfield {
  readonly texture: DataTexture;
  private readonly data = new Float32Array(SIZE * SIZE * 2);
  /** LOD-0 tiles with the height range of their solid ground (min > max: no ground at all). */
  private readonly tiles = new Map<number, { ti: number; tj: number; hm: Float32Array; min: number; max: number }>();
  private ox = Number.NaN;
  private oz = Number.NaN;
  private dirty = false;

  constructor() {
    this.texture = new DataTexture(this.data, SIZE, SIZE, RGFormat, FloatType);
    this.texture.minFilter = NearestFilter;
    this.texture.magFilter = NearestFilter;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
  }

  get size(): number {
    return SIZE;
  }

  /** LOD-0 tile data: (height, mask) pairs on a 65×65 1 m grid. */
  addTile(ti: number, tj: number, hm: Float32Array): void {
    let min = Infinity;
    let max = -Infinity;
    for (let k = 0; k < hm.length; k += 2) {
      if (hm[k + 1]! < 0.5) continue;
      const h = hm[k]!;
      if (h < min) min = h;
      if (h > max) max = h;
    }
    this.tiles.set(tileKey(ti, tj), { ti, tj, hm, min, max });
    if (this.inWindow(ti, tj)) this.copyTile(ti, tj, hm);
  }

  /**
   * Height range of the solid ground streamed in over the rectangle [x0, x1] × [z0, z1] (per
   * 64 m tile, so conservative). Returns false where nothing solid is loaded: nothing grows there.
   */
  groundRange(x0: number, z0: number, x1: number, z1: number, out: { min: number; max: number }): boolean {
    out.min = Infinity;
    out.max = -Infinity;
    for (let tj = Math.floor(z0 / TILE); tj <= Math.floor(z1 / TILE); tj++) {
      for (let ti = Math.floor(x0 / TILE); ti <= Math.floor(x1 / TILE); ti++) {
        const t = this.tiles.get(tileKey(ti, tj));
        if (!t || t.min > t.max) continue;
        if (t.min < out.min) out.min = t.min;
        if (t.max > out.max) out.max = t.max;
      }
    }
    return out.min <= out.max;
  }

  removeTile(ti: number, tj: number): void {
    this.tiles.delete(tileKey(ti, tj));
  }

  clear(): void {
    this.tiles.clear();
    this.data.fill(0);
    this.ox = Number.NaN;
    this.texture.needsUpdate = true;
  }

  /** Re-centre the window (snapped) and upload any changes. */
  update(cx: number, cz: number): void {
    const ox = Math.floor(cx / SNAP) * SNAP - SIZE / 2;
    const oz = Math.floor(cz / SNAP) * SNAP - SIZE / 2;
    if (ox !== this.ox || oz !== this.oz) {
      const px = this.ox;
      const pz = this.oz;
      this.ox = ox;
      this.oz = oz;
      const x1 = ox + SIZE - 1;
      const z1 = oz + SIZE - 1;
      if (Number.isNaN(px) || Math.abs(ox - px) >= SIZE || Math.abs(oz - pz) >= SIZE) this.refill(ox, oz, x1, z1);
      else {
        // Toroidal: texels still inside the window keep their data; only the strips that just
        // entered (which alias the ones that left) are cleared and refilled.
        if (ox > px) this.refill(px + SIZE, oz, x1, z1);
        else if (ox < px) this.refill(ox, oz, px - 1, z1);
        if (oz > pz) this.refill(ox, pz + SIZE, x1, z1);
        else if (oz < pz) this.refill(ox, oz, x1, pz - 1);
      }
      this.dirty = true;
    }
    if (this.dirty) {
      this.texture.needsUpdate = true;
      this.dirty = false;
    }
  }

  private inWindow(ti: number, tj: number): boolean {
    if (Number.isNaN(this.ox)) return false;
    const x0 = ti * TILE;
    const z0 = tj * TILE;
    return x0 + TILE >= this.ox && x0 <= this.ox + SIZE && z0 + TILE >= this.oz && z0 <= this.oz + SIZE;
  }

  /** Clear the world-metre rectangle [xa, xb] × [za, zb] (inside the window) and copy in the tiles over it. */
  private refill(xa: number, za: number, xb: number, zb: number): void {
    const d = this.data;
    for (let z = za; z <= zb; z++) {
      const row = (((z % SIZE) + SIZE) % SIZE) * SIZE;
      for (let x = xa; x <= xb; x++) {
        const o = (row + (((x % SIZE) + SIZE) % SIZE)) * 2;
        d[o] = 0;
        d[o + 1] = 0;
      }
    }
    for (const t of this.tiles.values()) {
      const x0 = t.ti * TILE;
      const z0 = t.tj * TILE;
      if (x0 + TILE < xa || x0 > xb || z0 + TILE < za || z0 > zb) continue;
      this.copyTile(t.ti, t.tj, t.hm, xa, za, xb, zb);
    }
  }

  private copyTile(ti: number, tj: number, hm: Float32Array, cxa = -Infinity, cza = -Infinity, cxb = Infinity, czb = Infinity): void {
    const n = TILE + 1;
    const x0 = ti * TILE;
    const z0 = tj * TILE;
    const xa = Math.max(x0, this.ox, cxa);
    const xb = Math.min(x0 + TILE, this.ox + SIZE - 1, cxb);
    const za = Math.max(z0, this.oz, cza);
    const zb = Math.min(z0 + TILE, this.oz + SIZE - 1, czb);
    const d = this.data;
    for (let z = za; z <= zb; z++) {
      const row = (((z % SIZE) + SIZE) % SIZE) * SIZE;
      const src = (z - z0) * n;
      for (let x = xa; x <= xb; x++) {
        const o = (row + (((x % SIZE) + SIZE) % SIZE)) * 2;
        const s = (src + (x - x0)) * 2;
        d[o] = hm[s]!;
        d[o + 1] = hm[s + 1]!;
      }
    }
    this.dirty = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
